import { type IngestionRun } from "@/shared/domain/ingestionRules";

/**
 * Where a session's ingestion stands. Mirrors the SDK `SessionIngestionState`.
 *
 * - `in_progress`: the session is in progress, so never announced ingested.
 * - `processing`: complete, with an ingestable file not ingested yet.
 * - `ingested`: complete, and every ingestable file is ingested.
 */
export type SessionIngestionState = "in_progress" | "processing" | "ingested";

/** An ingestable file in a session that is not ingested yet. */
export interface PendingIngestionFile {
  file_id: string;
  relative_path: string;
  ingestion_status: "not_ingested" | "partly_ingested" | "ingested";
  /** When the file's current version was uploaded. */
  uploaded: string | null;
  /** The latest run of an ingestion rule's trigger on the file, if any. */
  last_run: IngestionRun | null;
}

/** An ingestable, not-ingested file the session was told to stop waiting for. */
export interface SkippedIngestionFile {
  file_id: string;
  relative_path: string;
  skipped_at: string | null;
  skipped_by: string | null;
}

/** Response of `GET /v1/sessions/id/<id>/ingestion`. */
export interface SessionIngestionStatus {
  session_id: string;
  state: SessionIngestionState;
  ingestion_count: number;
  /** At most the first 100, ordered by path; `pending_file_count` counts them all. */
  pending_files: PendingIngestionFile[];
  pending_file_count: number;
  ingested_file_count: number;
  /** Files no ingestion rule matched at upload ("Stored as-is"); never waited for. */
  not_ingestable_file_count: number;
  /** At most the first 100, ordered by path; `skipped_file_count` counts them all. */
  skipped_files: SkippedIngestionFile[];
  /**
   * Every file the session was told not to wait for. Absent from a server that
   * does not send it; {@link skippedFileCount} falls back to `skipped_files`.
   */
  skipped_file_count?: number;
}

/** How many files the session was told not to wait for. */
export function skippedFileCount(
  status: Pick<SessionIngestionStatus, "skipped_files" | "skipped_file_count">,
): number {
  return status.skipped_file_count ?? status.skipped_files.length;
}

/** Response of `POST /v1/sessions/id/<id>/ingestion/skip`. */
export interface SkipWaitingResponse {
  ingestion: SessionIngestionStatus;
  /** Requested file ids not in the session, so nothing was skipped for them. */
  not_in_session: string[];
}

/**
 * Where one session's ingestion stands, in counts: what a sessions list shows
 * per row. Item of `POST /v1/sessions/ingestion/summaries`.
 */
export interface SessionIngestionSummary {
  session_id: string;
  state: SessionIngestionState;
  ingested_file_count: number;
  pending_file_count: number;
  not_ingestable_file_count: number;
  skipped_file_count: number;
  /**
   * Waiting files whose latest ingestion run failed or finished without
   * ingesting them, among the first 100 waiting files.
   */
  failed_file_count: number;
}

/** The most sessions one summaries request takes; the server's cap. */
export const MAX_INGESTION_SUMMARIES = 100;
